import { blinkConstants } from '@thoughtspot/blink-constants';
import {
    Action,
    HostEventContexts,
    IncomingEventType,
} from '@thoughtspot/embed-util';
import { create as createLogger } from '@thoughtspot/logger';

export const logger = createLogger('EventBridge');

export const EventType = blinkConstants.embedEvents as Record<string, string>;

export type MessageType = string;

export interface EventHandler {
    callback: (payload: any, context?: string) => any | Promise<any>;
    context: any;
    requiredParams: any;
}

export interface OnMessageEvent {
    data: {
        type: string;
        context: string;
        data: any;
    };
    ports?: readonly MessagePort[];
}

export const NON_MEMOIZED_EVENTS = [
    EventType.AuthExpire,
    EventType.ApiIntercept,
    EventType.RefreshAuthToken,
    EventType.Error,
];

// Reply is host-driven and not time-bounded (auth may need re-login/MFA;
// ApiIntercept has its own timeout). Exempt from the dispatch timeout.
export const NO_RESPONSE_TIMEOUT_EVENTS = new Set<string>([
    EventType.AuthExpire,
    EventType.RefreshAuthToken,
    EventType.ApiIntercept,
]);

export const rootLevelEvents = new Set([
    IncomingEventType.GetPageContext,
    IncomingEventType.UpdateCustomCss,
    IncomingEventType.GetIframeUrl,
    IncomingEventType.Navigate,
    IncomingEventType.UpdateRuntimeFilters,
    IncomingEventType.UpdateParameters,
    IncomingEventType.UiPassthrough,
    IncomingEventType.EmbedDestroyed,
    IncomingEventType.VisibleEmbedCoordinates,
    IncomingEventType.UpdateEmbedParams,
    IncomingEventType.Refresh,
    IncomingEventType.InfoSuccess,
    IncomingEventType.SetTokenGenerationConfig,
]);

/**
 * Events that are handled directly by components via postMessage listeners
 * rather than through the event bridge subscription system.
 * These events bypass the event bridge handler and are forwarded to component listeners.
 * @param eventType - The event type to check
 * @param context - The context to check
 * @returns True if the event should be handled by a component
 */
export const componentHandledEvents = new Map<string, string>([
    [Action.GetExportRequestForCurrentPinboard, 'liveboard'],
]);

export const ValidationErrors = {
    noHandlerRegistered: (context: string) =>
        `No handler registered for this event type in context "${context}". Please verify the event type is supported.`,
    noMatchingHandler: (context: string) =>
        `No matching handler found for this event type in context "${context}". Please verify the context is correct.`,
    contextMismatch: (type: string, expected: string, received: string) =>
        `Context mismatch for event "${type}": Expected context "${expected}" but received "${received}". Please use the correct context for this event.`,
    handlerExecutionError: (context: string, errorMessage: string) =>
        `Error executing handler in context "${context}": ${errorMessage}`,
    actionNotVisible: (actionName: string) =>
        `Action "${actionName}" is not visible or available in the current context`,
    missingVizIdGeneric:
        'Missing required parameter: vizId is required to trigger this action',
    missingVizId: (actionName: string) =>
        `Missing required parameter: vizId is required when calling "${actionName}" in liveboard context. To trigger for a specific visualization, provide its vizId.`,
    vizNotFound: (vizId: string) =>
        `Visualization with ID "${vizId}" not found in the liveboard. Please verify the vizId exists in the liveboard.`,
    vizNotLoaded: (vizId: string) =>
        `Visualization with ID "${vizId}" is not yet loaded. Please wait for the visualization to render before triggering this action.`,
    unexpectedVizId: (vizId: string) =>
        `Unexpected vizId "${vizId}" in event payload. This action does not require a vizId parameter in the current context.`,
};

// parent origin
/**
 * Event types for which the same payload can be dispatched repeatedly
 */
export const nonMemoizedEventTypes = new Set([
    EventType.CustomAction,
    EventType.EmbedIframeCenter,
    EventType.DialogOpen,
    EventType.DialogClose,
    EventType.Save,
    EventType.Download,
    EventType.DownloadAsPdf,
    EventType.DownloadAsCsv,
    EventType.DownloadAsXlsx,
    EventType.DownloadLiveboardAsContinuousPDF,
    EventType.Pin,
    EventType.SpotIQAnalyze,
    EventType.Share,
    EventType.ExportTML,
    EventType.SaveAsView,
    EventType.AnswerDelete,
    EventType.Alert,
    EventType.LiveboardInfo,
    EventType.SchedulesList,
    EventType.Present,
    EventType.Edit,
    EventType.EditLiveboard,
    EventType.EditVisualization,
    EventType.Cancel,
    EventType.Schedule,
    EventType.UpdateTML,
    EventType.EditTML,
    EventType.CopyLink,
    EventType.ShowUnderlyingData,
    EventType.Explore,
    EventType.CreateMonitor,
    EventType.ManageMonitor,
    EventType.MakeACopy,
    EventType.Remove,
    EventType.VizPointClick,
    EventType.VizPointDoubleClick,
    EventType.VizPointRightClick,
    EventType.GET_DATA_CLICK,
    EventType.Data,
    EventType.InsertInToSlide,
    EventType.Error,
    EventType.OnBeforeGetVizDataIntercept,
    EventType.ResetLiveboard,
    EventType.UpdatePersonalisedView,
    EventType.ChangePersonalisedView,
    EventType.ExitPresentMode,
    EventType.SpotterQueryTriggered,
    EventType.SpotterUsageLimitUpgrade,
    EventType.SpotterInit,
    EventType.AIHighlights,
    EventType.AddToCoaching,
    EventType.PreviewSpotterData,
    EventType.DataModelInstructions,
    EventType.SendTestScheduleEmail,
    EventType.RefreshLiveboardBrowserCache,
    EventType.SpotterVizInit,
    EventType.SpotterVizQueryTriggered,
    EventType.SpotterVizResponseComplete,
    EventType.SpotterVizCheckpointCreated,
    EventType.SpotterVizCheckpointRestored,
    EventType.SpotterVizError,
    EventType.SpotterVizClosed,
]);
